import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Ticket, DollarSign, Users, Plus, Clock, MapPin, ChevronRight, 
  ShieldCheck, ShieldAlert, Loader2, Share2, Link as LinkIcon, Copy, Check, 
  ExternalLink, QrCode, Music, Briefcase, Megaphone, UserCheck, ArrowUpRight,
  Newspaper, FileText, TrendingUp, Eye, Wallet, Receipt, Lock, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import ProducerLayout from '../../components/producer/ProducerLayout';
import NotificationBell from '../../components/NotificationBell';
import { useProducerAuth } from '../../context/ProducerAuthContext';
import { SubscriptionBannerFull } from '../../components/producer/SubscriptionBanner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

// Quick Action Card Component
const QuickActionCard = ({ icon: Icon, title, subtitle, to, color = "zinc", badge, onClick }) => {
  const colorClasses = {
    violet: "bg-violet-500/10 border-violet-500/30 hover:bg-violet-500/20",
    green: "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20",
    blue: "bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20",
    purple: "bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20",
    amber: "bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20",
    pink: "bg-pink-500/10 border-pink-500/30 hover:bg-pink-500/20",
    zinc: "bg-zinc-800 border-zinc-700 hover:bg-zinc-700/70",
  };
  
  const iconColorClasses = {
    violet: "text-violet-400",
    green: "text-emerald-400",
    blue: "text-blue-400",
    purple: "text-purple-400",
    amber: "text-amber-400",
    pink: "text-pink-400",
    zinc: "text-zinc-400",
  };

  const Content = (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`relative p-4 rounded-xl border transition-all cursor-pointer ${colorClasses[color]}`}
    >
      {badge && (
        <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-black/30`}>
          <Icon size={20} className={iconColorClasses[color]} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm truncate">{title}</p>
          <p className="text-xs text-zinc-500 truncate">{subtitle}</p>
        </div>
        <ArrowUpRight size={16} className="text-zinc-600" />
      </div>
    </motion.div>
  );

  if (onClick) {
    return <div onClick={onClick}>{Content}</div>;
  }

  return <Link to={to}>{Content}</Link>;
};

// Mini Stat Card Component
const MiniStatCard = ({ icon: Icon, label, value, subtext, color = "zinc" }) => {
  const colorClasses = {
    violet: "text-violet-400",
    green: "text-emerald-400",
    blue: "text-blue-400",
    purple: "text-purple-400",
    amber: "text-amber-400",
    pink: "text-pink-400",
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={colorClasses[color]} />
        <span className="text-zinc-500 text-xs uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtext && <p className="text-xs text-zinc-500 mt-1">{subtext}</p>}
    </div>
  );
};

const ProducerDashboardPage = () => {
  const navigate = useNavigate();
  const { hasPermission, isSecondaryAdmin, loading: authLoading } = useProducerAuth();
  const [producer, setProducer] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationRequirements, setVerificationRequirements] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [submittingVerification, setSubmittingVerification] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Determine if user can create events - consider loading state
  const cannotCreateEvent = !authLoading && isSecondaryAdmin && !hasPermission('manage_events');
  
  // Extended stats
  const [validators, setValidators] = useState([]);
  const [pressStats, setPressStats] = useState({ quotes: 0, contracts: 0, offers: 0 });
  const [djStats, setDjStats] = useState({ total: 0, active: 0 });
  const [jobStats, setJobStats] = useState({ offers: 0, applicants: 0 });

  const productionDomain = process.env.REACT_APP_PRODUCTION_DOMAIN || window.location.origin;
  const publicProfileUrl = producer?.slug 
    ? `${productionDomain}/productora/${producer.slug}` 
    : null;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('producer_token');
    if (!token) {
      navigate('/productoras/login');
      return;
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    try {
      // Main dashboard data
      const [producerRes, dashboardRes, verificationRes, requirementsRes] = await Promise.all([
        api.get('/producers/me'),
        api.get('/producers/me/dashboard'),
        api.get('/producers/me/verification-status'),
        api.get('/settings/verification-requirements')
      ]);
      
      setProducer(producerRes.data);
      setDashboard(dashboardRes.data);
      setVerificationStatus(verificationRes.data);
      setVerificationRequirements(requirementsRes.data);

      // Load extended stats (non-blocking)
      loadExtendedStats();
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('producer_token');
        navigate('/productoras/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadExtendedStats = async () => {
    try {
      // Load validators
      const validatorsRes = await api.get('/producers/me/validators').catch(() => ({ data: [] }));
      setValidators(Array.isArray(validatorsRes.data) ? validatorsRes.data : []);

      // Load press stats
      const [quotesRes, offersRes, contractsRes] = await Promise.all([
        api.get('/press/quotes/producer').catch(() => ({ data: [] })),
        api.get('/press/offers/producer').catch(() => ({ data: [] })),
        api.get('/press/contracts/producer').catch(() => ({ data: [] }))
      ]);
      
      const quotes = Array.isArray(quotesRes.data) ? quotesRes.data : [];
      const offers = Array.isArray(offersRes.data) ? offersRes.data : [];
      const contracts = Array.isArray(contractsRes.data) ? contractsRes.data : [];
      
      setPressStats({
        quotes: quotes.filter(q => q.status === 'pending' || q.status === 'quoted').length,
        offers: offers.filter(o => o.status === 'pending').length,
        contracts: contracts.filter(c => c.status === 'active').length
      });

      // Load DJ stats (if endpoint exists)
      try {
        const djRes = await api.get('/djs/suggested');
        const djs = Array.isArray(djRes.data) ? djRes.data : [];
        setDjStats({
          total: djs.length,
          active: djs.filter(d => d.is_available).length
        });
      } catch {
        setDjStats({ total: 0, active: 0 });
      }

      // Load job stats (if endpoint exists)
      try {
        const jobsRes = await api.get('/jobs/producer/listings');
        const jobs = Array.isArray(jobsRes.data) ? jobsRes.data : [];
        const activeJobs = jobs.filter(j => j.status === 'active');
        const totalApplicants = activeJobs.reduce((sum, j) => sum + (j.applicants_count || 0), 0);
        setJobStats({
          offers: activeJobs.length,
          applicants: totalApplicants
        });
      } catch {
        setJobStats({ offers: 0, applicants: 0 });
      }
    } catch (error) {
      console.error('Error loading extended stats:', error);
    }
  };

  const submitVerificationRequest = async () => {
    setSubmittingVerification(true);
    try {
      await api.post('/producers/me/request-verification', { message: verificationMessage });
      toast.success('Solicitud enviada correctamente');
      setShowVerificationModal(false);
      setVerificationMessage('');
      const res = await api.get('/producers/me/verification-status');
      setVerificationStatus(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmittingVerification(false);
    }
  };

  const copyPublicLink = async () => {
    if (!publicProfileUrl) return;
    try {
      await navigator.clipboard.writeText(publicProfileUrl);
      setLinkCopied(true);
      toast.success('Link copiado al portapapeles');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      toast.error('No se pudo copiar el link');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'America/Argentina/Buenos_Aires'
    });
  };
  // Returns the AR-day-of-month for an ISO date string. Avoids the bug where
  // Date.getDate() uses the browser TZ and can shift the day.
  const getEventDayAR = (dateString) => {
    if (!dateString) return '';
    try {
      const parts = new Intl.DateTimeFormat('es-AR', { day: 'numeric', timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date(dateString));
      return parts;
    } catch { return new Date(dateString).getDate(); }
  };

  if (loading) {
    return (
      <ProducerLayout currentPage="dashboard">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </ProducerLayout>
    );
  }

  const stats = dashboard?.stats || {};
  const upcomingEvents = dashboard?.upcoming_events || [];
  const totalPressBadge = pressStats.quotes + pressStats.offers + pressStats.contracts;

  return (
    <ProducerLayout currentPage="dashboard">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-black text-white">
              ¡Hola, {producer?.contact_name?.split(' ')[0] || 'Productor'}!
            </h1>
            <p className="text-zinc-400">Resumen de tu productora</p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell 
              userType="producer" 
              apiEndpoint="/producer/notifications" 
              title="Notificaciones" 
            />
            {/* Create Event button with RBAC */}
            {cannotCreateEvent ? (
              <Button
                disabled
                className="bg-zinc-800 text-zinc-500 cursor-not-allowed opacity-50 grayscale"
                title="No tenés permiso para crear eventos"
              >
                <Lock size={14} className="mr-1" />
                <Plus size={18} className="mr-2" />
                Crear Evento
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/producer/events/create')}
                className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                data-testid="create-event-btn"
              >
                <Plus size={18} className="mr-2" />
                Crear Evento
              </Button>
            )}
          </div>
        </div>

        {/* Verification Banner */}
        {!producer?.is_verified && verificationRequirements?.is_enabled !== false && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-amber-900/30 to-zinc-900 border border-amber-500/30 rounded-xl p-4"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="text-amber-500" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Cuenta no verificada</h3>
                  <p className="text-zinc-400 text-sm">
                    Verificá tu cuenta para obtener el badge de productora verificada.
                  </p>
                </div>
              </div>
              
              {verificationStatus?.pending_request ? (
                <div className="flex items-center gap-2 text-amber-400 bg-amber-500/10 px-4 py-2 rounded-lg">
                  <Clock size={16} />
                  <span className="text-sm font-medium">Solicitud en revisión</span>
                </div>
              ) : (
                <Button
                  onClick={() => setShowVerificationModal(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-black whitespace-nowrap"
                >
                  <ShieldCheck size={16} className="mr-2" />
                  Solicitar verificación
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* Super Productora Subscription Banner */}
        <SubscriptionBannerFull />

        {/* Share Public Profile Card */}
        {publicProfileUrl && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-violet-500/10 to-zinc-900 border border-violet-500/30 rounded-xl p-4"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Share2 className="text-violet-500" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Tu página pública</h3>
                  <p className="text-zinc-400 text-sm">Compartí tu perfil para que más personas conozcan tus eventos.</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 bg-black/50 px-3 py-2 rounded-lg border border-zinc-800 overflow-hidden">
                  <LinkIcon size={14} className="text-zinc-500 flex-shrink-0" />
                  <span className="text-zinc-400 text-sm truncate font-mono max-w-[200px]">
                    {publicProfileUrl.replace('https://', '')}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={copyPublicLink}
                    variant="outline"
                    size="sm"
                    className={`border-violet-500/50 ${linkCopied ? 'text-green-400' : 'text-violet-500'}`}
                  >
                    {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                  <a
                    href={publicProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-violet-600 to-pink-600 text-white text-sm font-medium rounded-md"
                  >
                    <ExternalLink size={14} className="mr-1" />
                    Ver
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStatCard
            icon={Calendar}
            label="Eventos"
            value={stats.total_events || 0}
            subtext={`${stats.published_events || 0} publicados`}
            color="violet"
          />
          <MiniStatCard
            icon={Ticket}
            label="Entradas"
            value={stats.total_tickets_sold || 0}
            subtext="vendidas"
            color="green"
          />
          <MiniStatCard
            icon={DollarSign}
            label="Ingresos"
            value={formatCurrency(stats.total_revenue)}
            subtext={`+${formatCurrency(stats.this_month_revenue)} este mes`}
            color="blue"
          />
          <MiniStatCard
            icon={Users}
            label="Comunidad"
            value={stats.community_size || 0}
            subtext="compradores únicos"
            color="purple"
          />
        </div>

        {/* Quick Actions Grid */}
        <div>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-violet-500" />
            Accesos Rápidos
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Liquidaciones - NEW */}
            <QuickActionCard
              icon={Wallet}
              title="Liquidaciones"
              subtitle="Ver mis ingresos"
              to="/producer/settlements"
              color="green"
            />
            
            {/* Servicios */}
            <QuickActionCard
              icon={Megaphone}
              title="Servicios"
              subtitle={totalPressBadge > 0 ? `${totalPressBadge} pendientes` : "Contratos y ofertas"}
              to="/producer/press"
              color="pink"
              badge={totalPressBadge > 0 ? totalPressBadge : null}
            />
            
            {/* DJs / Talentos */}
            <QuickActionCard
              icon={Music}
              title="DJs Sugeridos"
              subtitle={djStats.active > 0 ? `${djStats.active} disponibles` : "Encontrar talentos"}
              to="/producer/djs"
              color="purple"
            />
            
            {/* Portal Empleo */}
            <QuickActionCard
              icon={Briefcase}
              title="Portal Empleo"
              subtitle={jobStats.offers > 0 ? `${jobStats.offers} ofertas activas` : "Gestionar personal"}
              to="/producer/jobs"
              color="blue"
              badge={jobStats.applicants > 0 ? jobStats.applicants : null}
            />
            
            {/* Validadores */}
            <QuickActionCard
              icon={UserCheck}
              title="Validadores"
              subtitle={validators.length > 0 ? `${validators.length} activos` : "Gestionar equipo"}
              to="/producer/validators"
              color="amber"
            />
            
            {/* Escanear Accesos */}
            <QuickActionCard
              icon={QrCode}
              title="Escanear QR"
              subtitle="Control de accesos"
              to="/producer/scanner"
              color="green"
            />
            
            {/* Métricas Email */}
            <QuickActionCard
              icon={Eye}
              title="Métricas Email"
              subtitle="Analíticas de envíos"
              to="/producer/email-metrics"
              color="zinc"
            />

            {/* Consultas - NEW */}
            <QuickActionCard
              icon={MessageSquare}
              title="Consultas"
              subtitle="Pipeline de mesas a consultar"
              to="/producer/inquiries"
              color="violet"
            />

            {/* Administradores - NEW */}
            <QuickActionCard
              icon={ShieldCheck}
              title="Administradores"
              subtitle="Gestionar accesos"
              to="/producer/admins"
              color="violet"
            />
          </div>
        </div>

        {/* Two Column Layout: Events + Press Activity */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming Events */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <Calendar size={16} className="text-violet-500" />
                Próximos Eventos
              </h2>
              <Link to="/producer/events" className="text-violet-500 text-xs hover:underline flex items-center gap-1">
                Ver todos <ChevronRight size={12} />
              </Link>
            </div>

            {upcomingEvents.length === 0 ? (
              <div className="text-center py-8">
                <Calendar size={36} className="mx-auto text-zinc-700 mb-3" />
                <p className="text-zinc-500 text-sm mb-3">Sin eventos próximos</p>
                {cannotCreateEvent ? (
                  <Button
                    disabled
                    size="sm"
                    className="bg-zinc-800 text-zinc-500 cursor-not-allowed opacity-50 grayscale"
                  >
                    <Lock size={12} className="mr-1" />
                    <Plus size={14} className="mr-1" />
                    Crear Evento
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/producer/events/create')}
                    size="sm"
                    className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                  >
                    <Plus size={14} className="mr-1" />
                    Crear Evento
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.slice(0, 4).map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                    onClick={() => navigate(`/producer/events/${event.id}`)}
                    data-testid={`event-card-${event.id}`}
                  >
                    <div className="w-12 h-12 bg-violet-500/10 border border-violet-500/30 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-violet-500 text-[9px] uppercase font-bold">
                        {formatDate(event.date).split(' ')[0]}
                      </span>
                      <span className="text-white text-base font-bold leading-none">
                        {getEventDayAR(event.date)}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-sm truncate">{event.title}</h3>
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                        <span className="flex items-center gap-1 truncate">
                          <MapPin size={10} />
                          {event.location?.split(',')[0]}
                        </span>
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <Ticket size={10} />
                          {event.tickets_sold || 0}/{event.ticket_quantity || 0}
                        </span>
                      </div>
                    </div>

                    <ChevronRight size={16} className="text-zinc-600 flex-shrink-0" />
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Servicios Activity */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <Newspaper size={16} className="text-pink-400" />
                Actividad de Servicios
              </h2>
              <Link to="/producer/press" className="text-violet-500 text-xs hover:underline flex items-center gap-1">
                Ver todo <ChevronRight size={12} />
              </Link>
            </div>

            <div className="space-y-3">
              {/* Pending Quotes */}
              <Link 
                to="/producer/press?tab=quotes"
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <FileText size={16} className="text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Cotizaciones</p>
                    <p className="text-zinc-500 text-xs">Solicitudes pendientes</p>
                  </div>
                </div>
                <span className={`min-w-[28px] h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                  pressStats.quotes > 0 ? 'bg-amber-500 text-black' : 'bg-zinc-700 text-zinc-400'
                }`}>
                  {pressStats.quotes}
                </span>
              </Link>

              {/* Active Contracts */}
              <Link 
                to="/producer/press?tab=contracts"
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <ShieldCheck size={16} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Contratos Activos</p>
                    <p className="text-zinc-500 text-xs">En ejecución</p>
                  </div>
                </div>
                <span className={`min-w-[28px] h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                  pressStats.contracts > 0 ? 'bg-emerald-500 text-black' : 'bg-zinc-700 text-zinc-400'
                }`}>
                  {pressStats.contracts}
                </span>
              </Link>

              {/* Pending Offers */}
              <Link 
                to="/producer/press?tab=offers"
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Megaphone size={16} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Ofertas Enviadas</p>
                    <p className="text-zinc-500 text-xs">Esperando respuesta</p>
                  </div>
                </div>
                <span className={`min-w-[28px] h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                  pressStats.offers > 0 ? 'bg-purple-500 text-black' : 'bg-zinc-700 text-zinc-400'
                }`}>
                  {pressStats.offers}
                </span>
              </Link>

              {/* Validators Summary */}
              <Link 
                to="/producer/validators"
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <UserCheck size={16} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Validadores</p>
                    <p className="text-zinc-500 text-xs">Equipo de control</p>
                  </div>
                </div>
                <span className={`min-w-[28px] h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                  validators.length > 0 ? 'bg-blue-500 text-black' : 'bg-zinc-700 text-zinc-400'
                }`}>
                  {validators.length}
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Pending Approval Notice */}
        {stats.pending_events > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="text-yellow-500" size={20} />
            </div>
            <div className="flex-1">
              <p className="text-yellow-400 font-medium">
                {stats.pending_events} evento{stats.pending_events > 1 ? 's' : ''} pendiente{stats.pending_events > 1 ? 's' : ''} de aprobación
              </p>
              <p className="text-yellow-500/70 text-sm">Los eventos serán revisados por el equipo de Bravo</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Verification Requirements Modal */}
      <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="text-amber-500" />
              Requisitos para cuenta verificada
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 overflow-y-auto flex-1 min-h-0">
            {verificationRequirements?.instructions && (
              <p className="text-zinc-400 text-sm mb-4">{verificationRequirements.instructions}</p>
            )}
            
            <div className="space-y-3">
              {verificationRequirements?.requirements?.map((req, index) => (
                <div key={req.id || index} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-500 text-xs font-bold">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{req.title}</p>
                    <p className="text-zinc-500 text-xs mt-0.5">{req.description}</p>
                  </div>
                  {req.is_required && (
                    <span className="text-amber-500 text-[10px] font-bold uppercase flex-shrink-0">Req.</span>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-zinc-800 rounded-lg">
              <label className="text-zinc-400 text-sm block mb-2">Mensaje adicional (opcional)</label>
              <Textarea
                value={verificationMessage}
                onChange={(e) => setVerificationMessage(e.target.value)}
                placeholder="¿Algo que quieras agregar a tu solicitud?"
                className="bg-zinc-900 border-zinc-700 text-white"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 flex-shrink-0 pt-2 border-t border-zinc-800">
            <Button
              variant="outline"
              onClick={() => setShowVerificationModal(false)}
              className="border-zinc-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitVerificationRequest}
              disabled={submittingVerification}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {submittingVerification ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <ShieldCheck size={16} className="mr-2" />
              )}
              Solicitar verificación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProducerLayout>
  );
};

export default ProducerDashboardPage;
